import { useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { openLoans } from '../../../actions/homeowner'
import { selectNativeAccessFeature, selectDisableGetLeadsFeature } from '../../../store/selectors/customerProfile'
import { selectPaidInFull } from '../../../store/selectors/home/answers'
import { setIsShowingNativeAppCtaModal } from '../../../store/slices/modals'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { Icon } from '@homebotapp/hb-react-component-catalog'
import useViewport from '../../../hooks/useViewport'
import { HomeownerDigestTrackingEventLocation } from '../../../constants/tracking'
import { NavigationShortcutDatum } from '../../../types/navigation'

export const MSG = defineMessages({
  manageLoans: {
    id: 'HomeownerNavigationLinksOptions.manageLoans',
    defaultMessage: 'Manage Home Loan'
  },
  AddHome: {
    id: 'HomeownerNavigationLinksOptions.AddHome',
    defaultMessage: 'Add a Home'
  },
  share: {
    id: 'HomeownerNavigationLinksOptions.share',
    defaultMessage: 'Invite Friends to Join'
  },
  getApp: {
    id: 'HomeownerNavigationLinksOptions.getApp',
    defaultMessage: 'Get the App'
  }
})

const tileNames = {
  MANAGE_LOANS: 'Manage Home Loan',
  ADD_A_HOME: 'Add a Home',
  SHARE: 'Invite friends to join',
  GET_APP: 'Get the App'
} as const

export const useQuickActions = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const paidInFull = useSelector(selectPaidInFull)
  const hasValidLoan = !paidInFull
  const hasNativeAccess = useSelector(selectNativeAccessFeature)
  const disableGetLeadsFeature = useSelector(selectDisableGetLeadsFeature)
  const { isLargeOrAbove } = useViewport()
  const iconColor = 'brand.700'
  const iconWidth = '3'
  const iconHeight = '3'
  const trackingUIContext = isLargeOrAbove
    ? HomeownerDigestTrackingEventLocation.HOMEOWNER_DIGEST_SIDE_NAViGATION_PANEL
    : HomeownerDigestTrackingEventLocation.HOMEOWNER_DIGEST_MOBILE_SHORTCUTS_DRAWER_SELECT_COMPONENT

  const handleManageLoansClick = e => {
    // Hack to keep page from scrolling
    e.preventDefault()

    setTimeout(() => {
      dispatch(openLoans())
    }, 300)
  }

  const handleNativeAppClick = e => {
    e.preventDefault()

    dispatch(setIsShowingNativeAppCtaModal(true))
  }

  const quickActionOptions: NavigationShortcutDatum[] = [
    {
      // always show
      name: tileNames.MANAGE_LOANS,
      icon: <Icon name='dollar' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.manageLoans),
      onClick: handleManageLoansClick,
      tracking: {
        guid: 'rZD5PCNxY9DR1FVHN7g2kJ',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.MANAGE_LOANS}`
      }
    },
    {
      // always show, route to add home modal
      name: tileNames.ADD_A_HOME,
      icon: <Icon name='home-add' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.AddHome),
      href: OVERLAY_HELPERS.addHome.buildPath(),
      tracking: {
        guid: 'oyCaojAh4i9mgNepQYHbdF',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.ADD_A_HOME}`
      }
    },
    {
      // if disableGetLeadsFeature is false
      name: tileNames.SHARE,
      icon: <Icon name='users' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.share),
      href: OVERLAY_HELPERS.shareHomebot.buildPath(),
      tracking: {
        guid: 'ajj2qiqEYdJJUAAsmSbKcK',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.SHARE}`
      }
    },
    {
      // if native is available
      name: tileNames.GET_APP,
      icon: <Icon name='mobile' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.getApp),
      onClick: handleNativeAppClick,
      tracking: {
        guid: 'rEYv1s8M7V9itZeKnf89hz',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.GET_APP}`
      }
    }
  ]

  const filteredQuickActionsOptions = useMemo(
    () =>
      quickActionOptions.filter(tile => {
        if (tile.name === tileNames.GET_APP && !hasNativeAccess) return false
        if (tile.name === tileNames.SHARE && disableGetLeadsFeature) return false
        return true
      }),
    [hasNativeAccess, quickActionOptions, disableGetLeadsFeature]
  )

  return filteredQuickActionsOptions
}
